import { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function AddToOrgModal({ isOpen, onClose, onConfirm, orgTree = [], currentOrgIds = [] }) {
  const [search, setSearch] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [loading, setLoading] = useState(false);
  const backdropRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedOrgId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Filter out orgs the user is already in, then filter by search
  const availableOrgs = orgTree.filter(
    (org) => !currentOrgIds.includes(org.id)
  );
  const filteredOrgs = availableOrgs.filter((org) =>
    org.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = async () => {
    if (!selectedOrgId) return;
    setLoading(true);
    try {
      await onConfirm(selectedOrgId);
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
    >
      <div className="absolute inset-0 bg-deep-navy/60" />

      <div className="relative bg-white rounded-card shadow-card p-6 max-w-lg w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-deep-navy">Add to Organization</h3>
          <button onClick={onClose} className="min-h-touch min-w-touch flex items-center justify-center rounded-lg hover:bg-gray-100 transition-all">
            <XMarkIcon className="w-5 h-5 text-storm-blue" />
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-sky-blue" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organizations..."
            className="w-full pl-10 pr-4 py-3 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px]"
            autoFocus
          />
        </div>

        {/* Org list */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-2 mb-4">
          {filteredOrgs.length === 0 ? (
            <p className="text-center text-sky-blue py-8">
              {availableOrgs.length === 0
                ? 'User is already in all organizations'
                : 'No organizations match your search'}
            </p>
          ) : (
            filteredOrgs.map((org) => (
              <button
                key={org.id}
                onClick={() => setSelectedOrgId(org.id)}
                className={`w-full text-left px-4 py-3 min-h-touch rounded-xl border-2 transition-all ${
                  selectedOrgId === org.id
                    ? 'border-storm-blue bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <p className="font-semibold text-deep-navy">{org.name}</p>
                {org.tier && (
                  <span className="text-xs font-medium text-sky-blue">{org.tier}</span>
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="min-h-touch px-5 py-2.5 rounded-button font-semibold text-storm-blue bg-gray-100 hover:bg-gray-200 transition-all active:scale-[0.98]"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!selectedOrgId || loading}
            className={`min-h-touch px-5 py-2.5 rounded-button font-semibold text-white transition-all active:scale-[0.98] ${
              !selectedOrgId || loading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-button shadow-button'
            }`}
          >
            {loading ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
